import type { CVEditingSection, FullCV } from '@cibli/shared';

import { useState } from 'react';

import { useCVTranslation } from '@/features/cv/hooks/useCVTranslation.ts';

import type { CVViewMode } from '../../types';

import ComplementaryInfoSection from './ComplementaryInfoSection';
import ContactSection from './ContactSection';
import { formatFullName, getContrastTextColor } from './helpers';
import HobbiesSection from './HobbiesSection';
import LanguagesSection from './LanguagesSection';
import SkillsSection from './SkillsSection';

interface SidebarProps {
  cv: FullCV;
  mode: CVViewMode;
  onEdit?: (section: CVEditingSection) => void;
  accentColor?: string;
}

export default function Sidebar({ cv, onEdit, accentColor }: SidebarProps) {
  const { t } = useCVTranslation();
  const [photoError, setPhotoError] = useState(false);
  const fullName = formatFullName(cv.firstName, cv.lastName);
  const textColor = getContrastTextColor(accentColor ?? '#00B48A');
  const overlayColor = textColor === '#000000' ? 'rgba(0,0,0,0.12)' : 'rgba(255,255,255,0.12)';

  return (
    <aside
      className="w-[29%] min-h-full self-stretch px-[20px] py-[22px] flex flex-col gap-3.5"
      style={{ backgroundColor: 'transparent', color: textColor }}
      data-sidebar-text={textColor}
      data-sidebar-overlay={overlayColor}
    >
      {/* Photo ronde depuis pictureBase64 */}
      {cv.pictureBase64 && !photoError && (
        <div className="flex justify-center mb-1">
          <img
            src={
              cv.pictureBase64.startsWith('data:')
                ? cv.pictureBase64
                : `data:image/jpeg;base64,${cv.pictureBase64}`
            }
            alt={t('CV.profilePicture')}
            className="w-[100px] h-[100px] rounded-full object-cover border-[3px] border-white"
            onError={() => setPhotoError(true)}
          />
        </div>
      )}

      {/* Prénom + nom */}
      <div className="text-center mb-2">
        <h1 className="text-[15px] font-black uppercase tracking-wide leading-tight text-current">
          {fullName.toUpperCase()}
        </h1>
        {cv.jobTitle && (
          <p className="text-[20px] font-bold uppercase tracking-wide text-center text-current mt-1">
            {cv.jobTitle}
          </p>
        )}
      </div>

      {/* Contact */}
      <ContactSection
        email={cv.email}
        phone={cv.phone}
        location={cv.location}
        hasDriverLicense={cv.hasDriverLicense}
        driverLicenseTypes={cv.driverLicenseTypes}
        openToTraining={cv.openToTraining}
        onEdit={onEdit}
      />

      {/* Langues */}
      <LanguagesSection languages={cv.languages} onEdit={onEdit} />

      {/* Centres d'intérêt */}
      <HobbiesSection hobbies={cv.hobbiesAndInterests} onEdit={onEdit} overlayBg={overlayColor} />

      {/* Compétences */}
      <SkillsSection competences={cv.competences} onEdit={onEdit} />

      {/* Informations complémentaires courtes */}
      <ComplementaryInfoSection
        hasRQTH={cv.hasRQTH}
        mentions={cv.complementaryMentions}
        onEdit={onEdit}
      />
    </aside>
  );
}
